import { t } from "../../i18n";
import { usePaywall } from "../../billing/access";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ProjectInfo } from "@tasduvar/engine";
import { useProjectStore, type SeismicInit } from "../../state/projectStore";
import { closeCurrent } from "../../state/unsavedGuard";
import { projectStore, cloudStore, fromProjectFile, projectFileName, toProjectFile, type ProjectSummary } from "../../storage";
import { useAuth } from "../../auth/useAuth";
import { downloadJson, readJsonFile } from "../../lib/download";
import { errorMessage } from "../../api/client";
import { showcaseProject } from "./showcase";
import { applySmartStart, type SmartStart } from "../smart-start/smartStart";

/**
 * Başlangıç verisi ve eylemleri — vitrin (HomePage, misafir) ile CAD Başlangıç paftası (StartPage, oturum) ortak kullanır:
 * yerel/bulut proje listeleri, yeni proje / dosyadan aç / örnek proje, buluta taşı.
 * Tek proje açık: açan/oluşturan her eylem önce açık projeyi kapatır (`closeCurrent`; değişiklik varsa Kaydet / Kaydetme / İptal).
 */
export function useHomeProjects() {
  const nav = useNavigate();
  const { newProject, openProject } = useProjectStore();
  const { status, subscription } = useAuth();
  const authed = status === "authed";
  const [local, setLocal] = useState<ProjectSummary[]>([]);
  const [cloud, setCloud] = useState<ProjectSummary[] | null>(null);
  const [trash, setTrash] = useState<(ProjectSummary & { deletedAt: string })[]>([]);
  const [error, setError] = useState<string>();
  /** Proje sınırı doldu uyarısı (ücretli hesap; ücretsizde ödeme duvarı) — sınır sayısı */
  const [limitHit, setLimitHit] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const refresh = useCallback((): void => {
    void projectStore.list().then(setLocal);
    if (authed) { void cloudStore.list().then(setCloud).catch((e: unknown) => { setCloud([]); setError(errorMessage(e)); }); void cloudStore.listDeleted().then(setTrash).catch(() => setTrash([])); }
    else { setCloud(null); setTrash([]); }
  }, [authed]);
  useEffect(refresh, [refresh]);

  const store = authed ? cloudStore : projectStore;
  const origin = authed ? "cloud" : "local";
  const canCreate = !authed || subscription?.canWrite !== false;
  // Bulutta proje sınırı (sağlayıcı ayarı; sunucu da 402 PROJECT_LIMIT döner): ücretliye "dışa aktar + sil" uyarısı, ücretsize ödeme duvarı
  const maxProjects = authed ? subscription?.maxProjects ?? null : null;
  /** `adding`: eklenecek proje sayısı (toplu geri yüklemede seçilenlerin sayısı) */
  const atLimit = (adding = 1): boolean => {
    if (maxProjects === null || (cloud?.length ?? 0) + adding <= maxProjects) return false;
    if (subscription?.fullAccess) setLimitHit(maxProjects);
    else usePaywall.getState().open(`${t("Ücretsiz hesapta en çok")} ${maxProjects} ${t("proje saklanır; daha fazla proje")}`);
    return true;
  };
  const exportCloud = async (id: string): Promise<void> => {
    try { const p = await cloudStore.load(id); if (p) downloadJson(projectFileName(p), toProjectFile(p)); } catch (e) { setError(errorMessage(e)); }
  };
  /** `start` (Akıllı başlangıç): rapor/pencere değerleri duvara yazılır ve Akıllı tasarım paftasında tarama başlar */
  const create = (info: Partial<ProjectInfo>, seismic?: SeismicInit, start?: SmartStart): void => { if (atLimit()) return; closeCurrent(() => { let p = newProject(info, origin, seismic); if (start) p = applySmartStart(p, start); void store.save(p); nav(`/proje/${p.id}`); }); };
  const tryShowcase = (): void => { if (atLimit()) return; closeCurrent(() => { const p = showcaseProject(); openProject(p, origin); void store.save(p); nav(`/proje/${p.id}`); }); };
  const onFile = async (file: File | undefined): Promise<void> => {
    if (!file || atLimit()) return;
    try { const p = fromProjectFile(await readJsonFile(file)); closeCurrent(() => void (async () => { openProject(p, origin); await store.save(p); nav(`/proje/${p.id}`); })().catch((e: unknown) => setError(errorMessage(e)))); }
    catch (e) { setError(e instanceof Error ? e.message : "Dosya okunamadı"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  // Listeden zaten açık olan proje seçildiyse kapatmadan ona dön
  const already = (id: string): boolean => { if (useProjectStore.getState().project?.id !== id) return false; nav(`/proje/${id}`); return true; };
  const openLocal = async (id: string): Promise<void> => already(id) ? undefined : closeCurrent(() => void (async () => { const p = await projectStore.load(id); if (p) { openProject(p, "local"); nav(`/proje/${p.id}`); } })());
  const openCloud = async (id: string): Promise<void> => already(id) ? undefined : closeCurrent(() => void (async () => { const p = await cloudStore.load(id); if (p) { openProject(p, "cloud"); nav(`/proje/${p.id}`); } })());
  // Silme / geri alma eylemleri kimlik dizisi alır (tek satır ya da toplu seçim); sırayla çalışır, hata olursa durur, liste her durumda tazelenir
  const each = async (ids: string[], fn: (id: string) => Promise<unknown>): Promise<void> => {
    try { for (const id of ids) await fn(id); } catch (e) { setError(errorMessage(e)); } finally { refresh(); }
  };
  const removeLocal = (ids: string[]): Promise<void> => each(ids, (id) => projectStore.remove(id));
  const removeCloud = (ids: string[]): Promise<void> => each(ids, (id) => cloudStore.remove(id));
  // Silinenler: geri al (proje sınırına tabi) · kalıcı sil
  const restoreCloud = async (ids: string[]): Promise<void> => { if (!atLimit(ids.length)) await each(ids, (id) => cloudStore.restore(id)); };
  const purgeCloud = (ids: string[]): Promise<void> => each(ids, (id) => cloudStore.purge(id));
  const moveToCloud = async (id: string): Promise<void> => {
    const p = await projectStore.load(id); if (!p || atLimit()) return;
    try { await cloudStore.save(p); await projectStore.remove(id); refresh(); } catch (e) { setError(errorMessage(e)); }
  };
  return { authed, status, subscription, local, cloud, trash, restoreCloud, purgeCloud, error, maxProjects, limitHit, closeLimit: () => setLimitHit(null), atLimit, exportCloud, fileRef, refresh, canCreate, create, tryShowcase, onFile, openLocal, openCloud, removeLocal, removeCloud, moveToCloud };
}
